import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useConfirm } from "../ui";
import { NEW_ROW_ID, type EditingState, type RowEditorConfig } from "./rowEditor";

/**
 * Buffered row editing — the `EditorGrid` behaviour.
 *
 * Edits accumulate in a draft copy and only reach the server on save, so
 * cancelling costs nothing and a failed save leaves the row open with its
 * values intact rather than silently discarding what was typed.
 */
export function useRowEditor<T>(config: RowEditorConfig<T> | undefined, getRowId: (row: T) => string) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState<EditingState<T> | null>(null);

  const cancel = useCallback(() => setEditing(null), []);

  const startEdit = useCallback(
    (row: T) => {
      if (!config) return;
      // Structured clone keeps nested DTOs (credentials, settings) from being
      // edited by reference — otherwise cancelling would leave the mutation
      // behind in the cached list.
      const clone = structuredClone(row);
      setEditing({
        id: getRowId(row),
        draft: config.toDraft ? config.toDraft(clone) : clone,
        isNew: false,
        errors: {},
        saving: false,
      });
    },
    [config, getRowId],
  );

  const startAdd = useCallback(() => {
    if (!config?.newRow) return;
    setEditing({ id: NEW_ROW_ID, draft: config.newRow(), isNew: true, errors: {}, saving: false });
  }, [config]);

  const change = useCallback((draft: T) => {
    setEditing((current) => (current ? { ...current, draft } : current));
  }, []);

  // A ref, not editing.saving: two Enters in a row both run before the
  // re-render that would show the first one saving, and saved the row twice.
  const inFlight = useRef(false);

  const save = useCallback(
    async (options: { thenAdd?: boolean } = {}) => {
      if (!config || !editing || inFlight.current) return;

      const errors = config.validate?.(editing.draft, editing.isNew) ?? null;
      if (errors && Object.keys(errors).length > 0) {
        setEditing({ ...editing, errors });
        return;
      }

      setEditing({ ...editing, errors: {}, saving: true });
      inFlight.current = true;
      try {
        await config.onSave(editing.draft, editing.isNew);
        if (options.thenAdd && config.newRow) {
          setEditing({
            id: NEW_ROW_ID,
            draft: config.newRow(),
            isNew: true,
            errors: {},
            saving: false,
          });
        } else {
          setEditing(null);
        }
      } catch (error) {
        setEditing({ ...editing, saving: false });
        toast.error(error instanceof Error ? error.message : "Save failed");
      } finally {
        inFlight.current = false;
      }
    },
    [config, editing],
  );

  const remove = useCallback(
    async (row: T) => {
      if (!config?.onDelete) return;

      const copy = config.deleteConfirm?.(row) ?? { title: "Delete this row?" };
      const confirmed = await confirm({
        title: copy.title,
        ...(copy.message ? { message: copy.message } : {}),
        variant: "danger",
      });
      if (!confirmed) return;

      try {
        await config.onDelete(row);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Delete failed");
      }
    },
    [config, confirm],
  );

  return { editing, startEdit, startAdd, cancel, change, save, remove };
}
